import { Alert, Button, Skeleton } from 'antd';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAssistant } from '@entities/assistant';
import { extractErrorMessage } from '@shared/api';
import { TopBar } from '@shared/ui/v2';
import { ChannelsTab } from '@widgets/channels-tab';
import { AudienceTab } from './tabs/AudienceTab';
import { BroadcastsTab } from './tabs/BroadcastsTab';
import { KnowledgeBaseTab } from './tabs/KnowledgeBaseTab';
import { SettingsTab } from './tabs/SettingsTab';
import { StatisticsTab } from './tabs/StatisticsTab';
import { TestChatTab } from './tabs/TestChatTab';

/**
 * Раздел ассистента: /assistants/:id/:section (V2-4, этап 1 — каркас).
 *
 * Каждый раздел — отдельный адрес, поэтому прямая ссылка и обновление страницы
 * открывают именно его. Содержимое разделов пока прежнее; по этапам V2-4 оно
 * заменяется утверждёнными экранами 3–9 (`claude/Cabinet_v2_screens.md`).
 */
const SECTIONS: Record<string, { title: string; hint?: string }> = {
  settings: { title: 'Настройка', hint: 'Изменения применятся сразу во всех каналах' },
  'test-chat': { title: 'Тест-чат' },
  knowledge: { title: 'База знаний', hint: '0,5 ₽ за страницу, 1 ₽ за картинку, цену покажем до списания' },
  channels: {
    title: 'Каналы',
    hint: 'Где ассистент отвечает клиентам. Везде одна модель, инструкция и база знаний.',
  },
  dialogs: { title: 'Диалоги', hint: 'Переписка клиентов с ассистентом во всех каналах' },
  broadcasts: { title: 'Рассылки', hint: 'Сообщения подписчикам ваших ботов в Telegram и MAX' },
};

/** Старые адреса /assistants/:id?tab=… → новые разделы. */
const OLD_TABS: Record<string, string> = {
  knowledge: 'knowledge',
  'test-chat': 'test-chat',
  channels: 'channels',
  audience: 'broadcasts',
  broadcasts: 'broadcasts',
  statistics: 'dialogs',
  settings: 'settings',
};

export function AssistantRedirect() {
  const { id } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const section = OLD_TABS[sp.get('tab') ?? ''] ?? 'settings';
  return <Navigate to={`/assistants/${id}/${section}`} replace />;
}

export function AssistantSectionPage() {
  const navigate = useNavigate();
  const { id, section = 'settings' } = useParams<{ id: string; section: string }>();
  const { data: assistant, isLoading, error } = useAssistant(id);
  const meta = SECTIONS[section];

  if (!meta) return <Navigate to={`/assistants/${id}/settings`} replace />;

  return (
    <>
      <TopBar section title={meta.title} hint={meta.hint} />
      <div className="v2-content is-section">
        {isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
        {error && (
          <Alert
            type="error"
            showIcon
            message="Не удалось открыть ассистента"
            description={extractErrorMessage(error)}
            action={
              <Button size="small" onClick={() => navigate('/')}>
                На главную
              </Button>
            }
          />
        )}
        {assistant && (
          <div className="v2-card" style={{ padding: 20 }}>
            {section === 'settings' && <SettingsTab assistant={assistant} />}
            {section === 'test-chat' && <TestChatTab assistantId={assistant.id} />}
            {section === 'knowledge' && <KnowledgeBaseTab assistantId={assistant.id} />}
            {section === 'channels' && <ChannelsTab assistantId={assistant.id} />}
            {section === 'dialogs' && <StatisticsTab assistantId={assistant.id} />}
            {section === 'broadcasts' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <AudienceTab assistantId={assistant.id} />
                <BroadcastsTab assistantId={assistant.id} />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
